import { decodeEventLog, encodeFunctionData, erc20Abi, type Address, type Hex } from "viem";
import { ELYSIUM_TRADE, sameTradeAddress } from "../config/trade";
import { nativeV3PoolAbi, nativeV3RouterAbi } from "./native-v3";

export const MIN_SQRT_RATIO = 4295128739n;
export const MAX_SQRT_RATIO = 1461446703485210103287273052203988822378723970342n;
export type SwapSide = "BUY" | "SELL";
export type NativeQuote = { amountIn: bigint; amountOut: bigint; feeNative: bigint | null; sqrtPriceAfterX96?: bigint;
  tickAfter?: number; liquidityAfter?: bigint };
export type SwapIdentity = { account: Address; recipient: Address; side: SwapSide; maximum: bigint;
  minimum: bigint; priceLimit: bigint; deadline: bigint };

/** Let the pool price the requested budget across its range. Slippage protects output, not spot movement. */
export function swapPriceLimit(price: bigint, side: SwapSide): bigint {
  if (price <= MIN_SQRT_RATIO || price >= MAX_SQRT_RATIO) throw new Error("INVALID_SWAP_PRICE");
  const limit = side === "BUY" ? MAX_SQRT_RATIO - 1n : MIN_SQRT_RATIO + 1n;
  if (side === "BUY" ? limit <= price : limit >= price) throw new Error("NO_DIRECTIONAL_PRICE_ROOM");
  return limit;
}
/** Average execution versus pre-swap spot, excluding the pool fee; display only, in basis points. */
export function swapPriceImpactBps(quote: NativeQuote, price: bigint, side: SwapSide): bigint | null {
  if (price <= MIN_SQRT_RATIO || price >= MAX_SQRT_RATIO || quote.amountIn <= 0n || quote.amountOut <= 0n || quote.feeNative === null || quote.feeNative < 0n)
    return null;
  const spot = price * price, q192 = 1n << 192n;
  const expected = side === "BUY" ? (quote.amountIn - quote.feeNative) * q192 : quote.amountIn * spot;
  const actual = side === "BUY" ? quote.amountOut * spot : (quote.amountOut + quote.feeNative) * q192;
  if (expected <= 0n) return null;
  return (expected - actual) * 10000n / expected;
}
export function minimumSwapOutput(quote: NativeQuote, maximum: bigint, bps: number): bigint {
  if (!Number.isInteger(bps) || bps < 1 || bps > 500 || maximum <= 0n || maximum >= 1n << 256n ||
    quote.amountIn <= 0n || quote.amountIn > maximum || quote.amountOut <= 0n || quote.feeNative !== null && quote.feeNative < 0n)
    throw new Error("INVALID_OR_ZERO_SWAP_QUOTE");
  const minimum = quote.amountOut * BigInt(10000 - bps) / 10000n;
  if (minimum === 0n) throw new Error("ZERO_MINIMUM_OUTPUT");
  return minimum;
}
export function encodeNativeSwap(identity: SwapIdentity) {
  if (identity.maximum <= 0n || identity.minimum <= 0n || identity.deadline <= 0n ||
    identity.priceLimit <= MIN_SQRT_RATIO || identity.priceLimit >= MAX_SQRT_RATIO) throw new Error("INVALID_SWAP_REVIEW");
  return { to: ELYSIUM_TRADE.router, valueWei: identity.side === "BUY" ? identity.maximum : 0n,
    data: identity.side === "BUY" ? encodeFunctionData({ abi: nativeV3RouterAbi, functionName: "buy",
      args: [ELYSIUM_TRADE.token, identity.recipient, identity.minimum, identity.priceLimit, identity.deadline] }) :
      encodeFunctionData({ abi: nativeV3RouterAbi, functionName: "sell", args: [ELYSIUM_TRADE.token, identity.maximum,
        identity.recipient, identity.minimum, identity.priceLimit, identity.deadline] }) };
}
export function encodePoolApproval(maximum: bigint) {
  if (maximum <= 0n || maximum >= 1n << 256n) throw new Error("INVALID_APPROVAL_AMOUNT");
  return { to: ELYSIUM_TRADE.token, valueWei: 0n,
    data: encodeFunctionData({ abi: erc20Abi, functionName: "approve", args: [ELYSIUM_TRADE.pool, maximum] }) };
}
/** A receipt alone is insufficient. Match exactly one event to the reviewed trader and recipient. */
export function nativeSwapReceipt(logs: ReadonlyArray<{ address: Address; data: Hex; topics: readonly Hex[] }>, identity: SwapIdentity) {
  const matches: { amountIn: bigint; amountOut: bigint; feeNative: bigint; refund: bigint }[] = [];
  for (const log of logs) {
    if (!sameTradeAddress(log.address, ELYSIUM_TRADE.pool)) continue;
    try {
      const event = decodeEventLog({ abi: nativeV3PoolAbi, data: log.data, topics: [...log.topics] as [Hex, ...Hex[]] });
      if (event.eventName !== "Swap" || !sameTradeAddress(event.args.trader, identity.account) ||
        !sameTradeAddress(event.args.recipient, identity.recipient) || event.args.buy !== (identity.side === "BUY")) continue;
      const amountIn = event.args.buy ? event.args.nativeAmount : event.args.tokenAmount;
      const amountOut = event.args.buy ? event.args.tokenAmount : event.args.nativeAmount - event.args.feeNative;
      if (amountIn <= 0n || amountIn > identity.maximum || amountOut < identity.minimum ||
        event.args.feeNative > event.args.nativeAmount || event.args.sqrtPriceX96 <= MIN_SQRT_RATIO ||
        event.args.sqrtPriceX96 >= MAX_SQRT_RATIO) return null;
      matches.push({ amountIn, amountOut, feeNative: event.args.feeNative,
        refund: event.args.buy ? identity.maximum - amountIn : 0n });
    } catch { /* Unrelated logs do not establish execution. */ }
  }
  return matches.length === 1 ? matches[0]! : null;
}
